import { Select } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import type { Rule } from "antd/lib/form";
import { type FC, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { SelectInput } from "../../../Select/SelectInput";
import type { AntCustomFormField, SelectProfileFormField } from "../../types/types";
import { useFetchProfilesQuery } from "./serverApi";

interface ProfileSelectorProps extends AntCustomFormField<string> {
    name: string;
    label: string;
    readOnly?: boolean;
    rules?: Rule[];
    fieldConfig: SelectProfileFormField;
    fetchOptionsOnSelectOnly?: boolean;
    formInstance?: FormInstance | null;
}

export const ProfileSelector: FC<ProfileSelectorProps> = ({
    fieldConfig,
    readOnly,
    fetchOptionsOnSelectOnly = false,
    formInstance,
    onChange,
    ...restProps
}) => {
    const [active, setActive] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

    const { urlToFetchOptions, optionDisplayPropName, optionValuePropName, updateFields, useDataGroup, queryParams } = fieldConfig;
    const mustUseDataGroup = !!useDataGroup;

    const [searchParams] = useSearchParams();
    const params = {};

    if (queryParams) {
        queryParams.forEach((param) =>
            Object.keys(param).forEach((urlParam) => {
                // @ts-ignore
                const beParam = param[urlParam];
                // @ts-ignore
                params[beParam] = searchParams.get(urlParam);
            }),
        );
    }

    const urlSearchParams = new URLSearchParams(params).toString();

    let urlToFetch;
    if (mustUseDataGroup) {
        const dataGroup = getAssignedDataGroup();
        urlToFetch = `/${ResourceName.DATA_GROUPS}/${dataGroup?.code}${urlToFetchOptions}?${urlSearchParams}`;
    } else {
        urlToFetch = `${urlToFetchOptions}?${urlSearchParams}`;
    }

    const { data } = useFetchProfilesQuery(urlToFetch, {
        skip: (fetchOptionsOnSelectOnly && !active) || !urlToFetch,
    });

    const options = useMemo(() => data?.list || [], [data]);

    const onSelect = (id: string) => {
        if (onChange) {
            onChange(id);
        }
        setSelectedProfileId(id);
    };

    useEffect(() => {
        const profileData = options.find((obj) => obj.id === selectedProfileId);
        if (!formInstance || !profileData) return;

        formInstance.setFields(
            updateFields.map((field) => ({
                name: field,
                value: profileData?.[field],
            })),
        );
    }, [selectedProfileId, formInstance, options, updateFields]);

    const onClicked = () => setActive(true);

    return (
        <SelectInput onClick={onClicked} onChange={onSelect} disabled={readOnly} {...restProps}>
            {options.map((option: Record<string, string>) => {
                return (
                    <Select.Option
                        key={option[optionValuePropName]}
                        value={option[optionValuePropName]}
                        displayName={option[optionDisplayPropName]}
                    >
                        {option[optionDisplayPropName]}
                    </Select.Option>
                );
            })}
        </SelectInput>
    );
};
